/**
 * Legacy synergy migration helper
 * 
 * This helper creates scenario synergies that replicate the hardcoded behavior
 * for backward compatibility
 */

import { SynergyMapping } from '../types/ScenarioManagement';

/**
 * Create default synergies that match the hardcoded behavior
 */
export function createLegacySynergyMappings(): SynergyMapping[] {
  return [
    // Cyber Insurance synergies
    {
      id: 'legacy-cyber-insurance-financial',
      name: 'Cyber Insurance Financial Protection',
      description: 'Insurance helps with financial response options',
      requiredInvestmentIds: ['cyber-insurance'],
      effects: {
        responseBonuses: {
          financial: 30,  // 30% boost
        },
        impactMitigation: 10
      },
      conditions: {
        customCondition: 'response.label contains insurance|claim'
      },
      enabled: true,
      priority: 5
    },
    {
      id: 'legacy-cyber-insurance-legal',
      name: 'Cyber Insurance Legal Support',
      description: 'Insurance helps with legal/regulatory responses',
      requiredInvestmentIds: ['cyber-insurance'],
      effects: {
        responseBonuses: {
          financial: 20,
          reputation: 10
        }
      },
      conditions: {
        customCondition: 'response.label contains legal|regulatory'
      },
      enabled: true,
      priority: 5
    },
    
    // Incident Response Team synergies
    {
      id: 'legacy-irt-immediate',
      name: 'IRT Immediate Response',
      description: 'IRT helps with immediate response actions',
      requiredInvestmentIds: ['incident-response-team'],
      effects: {
        responseBonuses: {
          security: 25,
          operational: 15
        },
        impactMitigation: 10
      },
      conditions: {
        customCondition: 'response.label contains immediate|rapid|lockdown'
      },
      enabled: true,
      priority: 8
    },
    
    // Security Monitoring synergies
    {
      id: 'legacy-soc-monitoring',
      name: 'SOC Detection & Monitoring',
      description: 'SOC helps with detection and monitoring responses',
      requiredInvestmentIds: ['security-monitoring'],
      effects: {
        responseBonuses: {
          security: 30,
          operational: 20
        },
        impactMitigation: 5
      },
      conditions: {
        customCondition: 'response.label contains monitor|detect|hunt'
      },
      enabled: true,
      priority: 7
    },
    
    // Backup & Recovery synergies
    {
      id: 'legacy-backup-recovery',
      name: 'Backup Recovery System',
      description: 'Backup helps with recovery and restoration responses',
      requiredInvestmentIds: ['backup-recovery'],
      effects: {
        responseBonuses: {
          operational: 40,
          financial: 20
        },
        impactMitigation: 15
      },
      conditions: {
        customCondition: 'response.label contains restore|recover|backup'
      },
      enabled: true,
      priority: 9
    },
    {
      id: 'legacy-backup-ransomware',
      name: 'Backup Ransomware Protection',
      description: 'Backup especially effective against ransomware',
      requiredInvestmentIds: ['backup-recovery'],
      effects: {
        impactMitigation: 10
      },
      conditions: {
        incidentCategories: ['ransomware']
      },
      enabled: true,
      priority: 10
    },
    
    // Crisis Response Team synergies
    {
      id: 'legacy-crisis-team',
      name: 'Crisis Team Coordination',
      description: 'Crisis team helps with communication and coordination',
      requiredInvestmentIds: ['crisis-response-team'],
      effects: {
        responseBonuses: {
          reputation: 35,
          operational: 20
        },
        impactMitigation: 10
      },
      conditions: {
        customCondition: 'response.label contains communication|coordinate|crisis'
      },
      enabled: true,
      priority: 8
    },
    
    // Legal Defense Fund synergies
    {
      id: 'legacy-legal-fund',
      name: 'Legal Defense Support',
      description: 'Legal fund helps with regulatory and legal responses',
      requiredInvestmentIds: ['legal-defense-fund'],
      effects: {
        responseBonuses: {
          financial: 30,
          reputation: 20
        },
        impactMitigation: 5
      },
      conditions: {
        customCondition: 'response.label contains legal|regulatory|compliance'
      },
      enabled: true,
      priority: 6
    },
    
    // AI Threat Detection synergies
    {
      id: 'legacy-ai-detection',
      name: 'AI Threat Analysis',
      description: 'AI helps with advanced threat hunting and analysis',
      requiredInvestmentIds: ['ai-threat-detection'],
      effects: {
        responseBonuses: {
          security: 35
        },
        impactMitigation: 10
      },
      conditions: {
        customCondition: 'response.label contains forensic|analysis|hunt'
      },
      enabled: true,
      priority: 8
    },
    
    // Zero Trust Architecture synergies
    {
      id: 'legacy-zero-trust',
      name: 'Zero Trust Segmentation',
      description: 'Zero trust helps with segmentation and isolation',
      requiredInvestmentIds: ['zero-trust-architecture'],
      effects: {
        responseBonuses: {
          security: 40,
          operational: 20
        },
        impactMitigation: 15
      },
      conditions: {
        customCondition: 'response.label contains segment|isolat|zero'
      },
      enabled: true,
      priority: 10
    },
    
    // Customer Retention Program synergies
    {
      id: 'legacy-customer-retention',
      name: 'Customer Communication',
      description: 'Customer program helps with public communication',
      requiredInvestmentIds: ['customer-retention-program'],
      effects: {
        responseBonuses: {
          reputation: 40,
          financial: 20
        }
      },
      conditions: {
        customCondition: 'response.label contains customer|public|stakeholder'
      },
      enabled: true,
      priority: 7
    },
    
    // Reputation Recovery synergies
    {
      id: 'legacy-reputation-recovery',
      name: 'Reputation Management',
      description: 'Reputation program enhances all communication responses',
      requiredInvestmentIds: ['reputation-recovery'],
      effects: {
        responseBonuses: {
          reputation: 35
        },
        impactMitigation: 5
      },
      conditions: {
        customCondition: 'response.label contains communication|statement|public'
      },
      enabled: true,
      priority: 7
    }
  ];
}

/**
 * Check if synergies should use legacy mappings
 */
export function shouldUseLegacySynergies(
  scenarioSynergies?: SynergyMapping[],
  enableSynergies?: boolean
): boolean {
  // Use legacy if synergies are enabled but no scenario synergies provided
  return enableSynergies === true && (!scenarioSynergies || scenarioSynergies.length === 0);
}